/**
 * Utility functions for aspect ratio validation
 */

import { PlacementAnalysis } from "./types";

// Meta placement to aspect ratio mapping (simplified for the two key ratios)
const PLACEMENT_ASPECT_RATIOS: Record<string, string[]> = {
  // Feed placements that support square (1:1)
  facebook_feed: ["1.91:1", "16:9", "1:1", "4:5"],
  facebook_profile_feed: ["1.91:1", "1:1", "4:5"],
  threads_feed: ["1.91:1", "16:9", "1:1", "4:5", "2:3", "9:16"],
  instagram_feed: ["1.91:1", "16:9", "1:1", "4:5", "2:3", "9:16"],
  facebook_marketplace: ["1.91:1", "1:1", "4:5"],
  facebook_video_feeds: ["1.91:1", "1:1", "4:5", "2:3"],
  facebook_right_column: ["1.91:1", "1:1"],
  instagram_explore: ["1.91:1", "16:9", "1:1", "9:16"],
  instagram_explore_home: ["1.91:1", "1:1", "4:5"],
  facebook_business_explore: ["1.91:1", "1:1", "4:5"],

  // Stories, status and reels that support portrait (9:16)
  instagram_stories: ["1.91:1", "16:9", "1:1", "4:5", "9:16"],
  facebook_stories: ["1.91:1", "16:9", "1:1", "4:5", "9:16"],
  messenger_stories: ["9:16"],
  instagram_reels: ["1.91:1", "16:9", "1:1", "4:5", "2:3", "9:16"],
  facebook_reels: ["1.91:1", "16:9", "1:1", "4:5", "2:3", "9:16"],
  whatsapp_status: ["1.91:1", "16:9", "1:1", "4:5", "9:16"],

  // In-stream ads for reels
  facebook_instream_reels: ["1.91:1", "1:1", "4:5"],
  facebook_reels_ads: ["1.91:1", "1:1", "9:16"],

  // Search results
  facebook_search_results: ["1.91:1", "1:1", "9:16"],
  instagram_search_results: ["1.91:1", "16:9", "1:1", "4:5"],

  // Apps and sites
  audience_network_native: ["1.91:1", "1:1", "4:5"],
  audience_network_banner: ["1.91:1", "1:1", "4:5"],
  audience_network_interstitial: ["1.91:1", "1:1", "4:5"],
  audience_network_rewarded_videos: ["1.91:1", "1:1"],
};

/**
 * Analyzes a set of placements to determine which aspect ratios are needed
 * @param placements - Array of Meta placement keys
 * @returns Analysis of which aspect ratios are needed
 */
export function analyzePlacementRequirements(
  placements: string[]
): PlacementAnalysis {
  const needsSquare = new Set<string>();
  const needsPortrait = new Set<string>();
  const needsLandscape = new Set<string>();
  const uncoveredPlacements: string[] = [];

  for (const placement of placements) {
    const supportedRatios = PLACEMENT_ASPECT_RATIOS[placement];

    if (!supportedRatios) {
      uncoveredPlacements.push(placement);
      continue;
    }

    if (supportedRatios.includes("1:1")) {
      needsSquare.add(placement);
    }
    if (supportedRatios.includes("9:16")) {
      needsPortrait.add(placement);
    }
    if (supportedRatios.includes("1.91:1")) {
      needsLandscape.add(placement);
    }
  }

  const totalPlacements = placements.length;
  const coveredPlacements = totalPlacements - uncoveredPlacements.length;
  const coverage =
    totalPlacements > 0 ? (coveredPlacements / totalPlacements) * 100 : 0;

  const recommendedImages: string[] = [];
  if (needsSquare.size > 0) recommendedImages.push("Square (1:1)");
  if (needsPortrait.size > 0) recommendedImages.push("Portrait (9:16)");
  if (needsLandscape.size > 0) recommendedImages.push("Landscape (1.91:1)");

  return {
    needsSquare: needsSquare.size > 0,
    needsPortrait: needsPortrait.size > 0,
    needsLandscape: needsLandscape.size > 0,
    coverage,
    uncoveredPlacements,
    recommendedImages,
  };
}

/**
 * Converts a numeric aspect ratio to fractional format (e.g., 0.8 -> "4:5", 1.0 -> "1:1")
 */
export function formatAspectRatio(ratio: number): string {
  // Common aspect ratios and their fractional representations
  const commonRatios = [
    { ratio: 1.91, fraction: "1.91:1" },
    { ratio: 1.78, fraction: "16:9" },
    { ratio: 1.0, fraction: "1:1" },
    { ratio: 0.8, fraction: "4:5" },
    { ratio: 0.67, fraction: "2:3" },
    { ratio: 0.56, fraction: "9:16" },
    { ratio: 1.25, fraction: "5:4" },
    { ratio: 1.33, fraction: "4:3" },
    { ratio: 0.75, fraction: "3:4" },
    { ratio: 1.5, fraction: "3:2" },
  ];

  // Check if the ratio matches a common aspect ratio (within 0.01 tolerance)
  for (const commonRatio of commonRatios) {
    if (Math.abs(ratio - commonRatio.ratio) < 0.01) {
      return commonRatio.fraction;
    }
  }

  // If no common ratio matches, calculate the simplest fraction
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

  // Convert to fraction by finding the greatest common divisor
  const precision = 1000; // For 3 decimal places
  const numerator = Math.round(ratio * precision);
  const denominator = precision;
  const divisor = gcd(numerator, denominator);

  const simplifiedNum = numerator / divisor;
  const simplifiedDen = denominator / divisor;

  return `${simplifiedNum}:${simplifiedDen}`;
}

/**
 * Gets the dimensions of an image or video file
 */
export async function getFileDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");

    if (!isVideo && !isImage) {
      reject(new Error("File must be an image or video"));
      return;
    }

    const url = URL.createObjectURL(file);

    if (isImage) {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Failed to load image"));
      };
      img.src = url;
    } else {
      const video = document.createElement("video");
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve({ width: video.videoWidth, height: video.videoHeight });
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Failed to load video"));
      };
      video.src = url;
      video.load();
    }
  });
}
